import { Avatar, AvatarFallback } from '@workspace/ui/components/avatar'
import { cn } from '@workspace/ui/lib/utils'
import { Check, ChevronDown } from 'lucide-react'
import { useRef, useState } from 'react'

export interface StartupSwitcherItem {
  id: string
  name: string
  initials: string
  role?: 'OWNER' | 'WRITE' | 'READ'
}

export interface StartupSwitcherProps {
  active: StartupSwitcherItem
  startups: StartupSwitcherItem[]
  onSwitch: (startupId: string) => void
  /** Called when clicking the name — use for navigation to dashboard */
  onNameClick?: () => void
}

export function StartupSwitcher({ active, startups, onSwitch, onNameClick }: StartupSwitcherProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const showDropdown = startups.length > 1

  return (
    <div className="relative flex items-center" ref={ref}>
      <button
        type="button"
        className="flex items-center gap-1.5 rounded-lg px-2 py-1 text-sm font-medium text-foreground transition-colors hover:bg-card"
        onClick={() => onNameClick?.()}
      >
        <Avatar className="size-5">
          <AvatarFallback className="bg-[#f97316]/15 text-[8px] font-bold text-[#f97316]">
            {active.initials}
          </AvatarFallback>
        </Avatar>
        {active.name}
      </button>
      {showDropdown && (
        <button
          type="button"
          className="flex items-center rounded-lg p-1 text-muted-foreground transition-colors hover:bg-card hover:text-foreground"
          onClick={() => setOpen(!open)}
          aria-label="Switch startup"
        >
          <ChevronDown className="size-3" />
        </button>
      )}

      {open && showDropdown && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setOpen(false)}
            aria-label="Close dropdown"
            tabIndex={-1}
          />
          <div className="absolute left-0 top-full z-50 mt-1 min-w-[200px] overflow-hidden rounded-lg border border-border bg-card shadow-lg">
            {startups.map((startup) => (
              <button
                key={startup.id}
                type="button"
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-muted/50',
                  startup.id === active.id && 'bg-muted/30'
                )}
                onClick={() => {
                  setOpen(false)
                  if (startup.id !== active.id) {
                    onSwitch(startup.id)
                  }
                }}
              >
                <Avatar className="size-5">
                  <AvatarFallback className="bg-[#f97316]/15 text-[8px] font-bold text-[#f97316]">
                    {startup.initials}
                  </AvatarFallback>
                </Avatar>
                <span className="flex-1 text-left text-foreground">{startup.name}</span>
                {startup.role && (
                  <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium capitalize text-muted-foreground">
                    {startup.role.toLowerCase()}
                  </span>
                )}
                {startup.id === active.id && <Check className="size-3.5 text-[#f97316]" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
